import { useMutateBookMeeting } from '@/hooks/mutation/useMutateBookMeeting';
import { useQueryMeetingAvailability } from '@/hooks/query/useQueryMeetingAvailability';
import { useCallback, useMemo, useState } from 'react';

import { extractMeetingSlug, getUserTimezone } from '../meeting-booker-utils';
import { type TimeSlot } from '../meeting-booker.types';

export function useMeetingBooker(meetingUrl: string) {
  const slug = useMemo(() => extractMeetingSlug(meetingUrl), [meetingUrl]);
  const timezone = useMemo(() => getUserTimezone(), []);

  const [selectedDate, setSelectedDate] = useState<Date | undefined>();
  const [selectedSlot, setSelectedSlot] = useState<TimeSlot | undefined>();
  const [selectedDuration, setSelectedDuration] = useState<number | undefined>();
  const [monthOffset, setMonthOffset] = useState(0);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  const { data: availabilityResponse, isLoading: isLoadingAvailability } =
    useQueryMeetingAvailability(slug, monthOffset, timezone);

  const availability = availabilityResponse?.data;
  const durations = availability?.durations ?? [];
  const activeDuration = selectedDuration ?? durations[0] ?? 1800000;

  // Group slots by date in user's timezone
  const slotsByDate = useMemo(() => {
    if (!availability?.availabilities) return new Map<string, TimeSlot[]>();

    const map = new Map<string, TimeSlot[]>();
    for (const slot of availability.availabilities) {
      const date = new Date(slot.startMillisUtc);
      const dateKey = date.toLocaleDateString('en-CA', { timeZone: timezone });
      const existing = map.get(dateKey) ?? [];
      existing.push(slot);
      map.set(dateKey, existing);
    }
    return map;
  }, [availability?.availabilities, timezone]);

  // Dates that have available slots
  const availableDates = useMemo(
    () =>
      new Set(
        [...slotsByDate.keys()].map((dateStr) => {
          const parts = dateStr.split('-').map(Number);
          return new Date(parts[0]!, parts[1]! - 1, parts[2]).toDateString();
        })
      ),
    [slotsByDate]
  );

  // Slots for the selected date
  const slotsForSelectedDate = useMemo(() => {
    if (!selectedDate) return [];
    const dateKey = selectedDate.toLocaleDateString('en-CA', { timeZone: timezone });
    return slotsByDate.get(dateKey) ?? [];
  }, [selectedDate, slotsByDate, timezone]);

  const bookMutation = useMutateBookMeeting();

  const handleSelectDate = useCallback((date: Date | undefined) => {
    setSelectedDate(date);
    setSelectedSlot(undefined);
  }, []);

  const handleSelectSlot = useCallback((slot: TimeSlot) => {
    setSelectedSlot(slot);
    setIsCalendarOpen(false);
  }, []);

  const handleSelectDuration = useCallback((duration: number) => {
    setSelectedDuration(duration);
    setSelectedSlot(undefined);
  }, []);

  const handleBook = useCallback(
    async (
      firstName: string,
      lastName: string,
      email: string,
      formFields?: Record<string, string>
    ) => {
      if (!slug || !selectedSlot) return;

      await bookMutation.mutateAsync({
        slug,
        duration: activeDuration,
        startTime: selectedSlot.startMillisUtc,
        firstName,
        lastName,
        email,
        timezone,
        formFields,
      });
    },
    [slug, selectedSlot, activeDuration, timezone, bookMutation]
  );

  const clearSelection = useCallback(() => {
    setSelectedDate(undefined);
    setSelectedSlot(undefined);
    setIsCalendarOpen(false);
  }, []);

  return {
    slug,
    timezone,
    durations,
    activeDuration,
    selectedDate,
    selectedSlot,
    monthOffset,
    isCalendarOpen,
    isLoadingAvailability,
    availableDates,
    slotsForSelectedDate,
    isBooking: bookMutation.isPending,
    bookingError: bookMutation.error?.message,
    isBooked: bookMutation.isSuccess,
    setIsCalendarOpen,
    setMonthOffset,
    clearSelection,
    handleSelectDate,
    handleSelectSlot,
    handleSelectDuration,
    handleBook,
  };
}
